import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Badge } from "react-bootstrap";
import { Table, Dropdown, Menu } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { getTaskById, updateAssignmentStatus } from "../../../services/taskServices";
import { decrypt } from "../../../utils/crypto";
import Loading from "../../../components/Loading";

const TaskDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Extract encrypted task ID from pathname since URL format is /create-task/id=value
  const encryptedTaskId = useMemo(() => {
    const pathname = location.pathname;
    const match = pathname.match(/id=([^/]+)$/);
    return match ? match[1] : id;
  }, [location.pathname, id]);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    console.log("Raw ID from params:", id);
    console.log("Encrypted Task ID:", encryptedTaskId);

    const fetchTaskDetails = async () => {
      try {
        setLoading(true);

        // Decrypt task ID from URL
        const taskId = decrypt(encryptedTaskId);
        console.log("Decrypted Task ID:", taskId);

        if (!taskId) {
          console.log("Invalid task ID, setting loading to false");
          setLoading(false);
          return;
        }

        // Fetch task details
        const taskResponse = await getTaskById(taskId);

        if (taskResponse.success) {
          // Ensure assignments is always an array
          const taskData = {
            ...taskResponse.task,
            assignments: Array.isArray(taskResponse.task.assignments) 
              ? taskResponse.task.assignments 
              : []
          };
          setTask(taskData);
        } else {
          throw new Error(taskResponse.message || "Failed to fetch task");
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
        toast.error(t("Error fetching task details"));
      } finally {
        setLoading(false);
      }
    };

    if (encryptedTaskId) {
      fetchTaskDetails();
    } else {
      console.log("No encrypted task ID found");
      setLoading(false);
    }
  }, [encryptedTaskId, navigate, t, id]);



  const handleAssignmentStatusUpdate = React.useCallback(async (assignmentId, statusType, newStatus) => {
    try {
      setUpdating(true);
      // Convert UI field names to API field names
      const apiFieldName = statusType === 'assigned_task_status' ? 'task_status' : 'payment_status';
      const statusData = { [apiFieldName]: newStatus };
      
      const response = await updateAssignmentStatus(assignmentId, statusData);

      if (response.success) {
        // Update the specific assignment in the task state
        setTask((prev) => ({
          ...prev,
          assignments: Array.isArray(prev.assignments) 
            ? prev.assignments.map((assignment) =>
                assignment.assignment_id === assignmentId
                  ? { ...assignment, [statusType]: newStatus }
                  : assignment
              )
            : [],
        }));
        
        const statusLabel = statusType === 'assigned_task_status' ? t('Task status') : t('Payment status');
        toast.success(t(`${statusLabel} updated successfully`));
      } else {
        toast.error(t("Failed to update status"));
      }
    } catch (error) {
      console.error("Error updating assignment status:", error);
      toast.error(t("Error updating status"));
    } finally {
      setUpdating(false);
    }
  }, [t]);
 
  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { bg: "danger", text: t("High") },
      medium: { bg: "warning", text: t("Medium") },
      low: { bg: "success", text: t("Low") },
    };

    const config = priorityConfig[priority] || {
      bg: "secondary",
      text: priority,
    };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const taskStatusOptions = React.useMemo(() => [
    { key: "ASSIGNED", label: t("Assigned"), bg: "warning" },
    { key: "IN_PROGRESS", label: t("In Progress"), bg: "info" },
    { key: "COMPLETED", label: t("Completed"), bg: "success" },
    { key: "CANCELLED", label: t("Cancelled"), bg: "danger" },
  ], [t]);

  const paymentStatusOptions = React.useMemo(() => [
    { key: "PENDING", label: t("Pending"), bg: "warning" },
    { key: "PAID", label: t("Paid"), bg: "success" },
    { key: "CANCELLED", label: t("Cancelled"), bg: "danger" },
  ], [t]);

  const getAssignmentStatusBadge = React.useCallback((status, assignmentId) => {
    // Handle null/undefined status
    const statusValue = status || 'ASSIGNED';
    const currentStatus = taskStatusOptions.find(opt => opt.key === statusValue) || 
                         { key: statusValue, label: statusValue, bg: "secondary" };
    
    const statusMenu = (
      <Menu
        onClick={({ key }) => handleAssignmentStatusUpdate(assignmentId, 'assigned_task_status', key)}
      >
        {taskStatusOptions
          .filter(option => option.key !== statusValue)
          .map(option => (
            <Menu.Item key={option.key}>
              <span className={`badge bg-${option.bg} me-2`}>{option.label}</span>
            </Menu.Item>
          ))}
      </Menu>
    );

    return (
      <Dropdown overlay={statusMenu} trigger={['click']} disabled={updating}>
        <span className={`badge bg-${currentStatus.bg}`} style={{ cursor: 'pointer' }}>
          {currentStatus.label}
          <i className="fa fa-caret-down ms-1"></i>
        </span>
      </Dropdown>
    );
  }, [taskStatusOptions, handleAssignmentStatusUpdate, updating]);

  const getAssignmentPaymentBadge = React.useCallback((status, assignmentId) => {
    // Handle null/undefined status
    const statusValue = status || 'PENDING';
    const currentStatus = paymentStatusOptions.find(opt => opt.key === statusValue) || 
                         { key: statusValue, label: statusValue, bg: "secondary" };
    
    const paymentMenu = (
      <Menu
        onClick={({ key }) => handleAssignmentStatusUpdate(assignmentId, 'assigned_payment_status', key)}
      >
        {paymentStatusOptions
          .filter(option => option.key !== statusValue)
          .map(option => (
            <Menu.Item key={option.key}>
              <span className={`badge bg-${option.bg} me-2`}>{option.label}</span>
            </Menu.Item>
          ))}
      </Menu>
    );

    return (
      <Dropdown overlay={paymentMenu} trigger={['click']} disabled={updating}>
        <span className={`badge bg-${currentStatus.bg}`} style={{ cursor: 'pointer' }}>
          {currentStatus.label}
          <i className="fa fa-caret-down ms-1"></i>
        </span>
      </Dropdown>
    );
  }, [paymentStatusOptions, handleAssignmentStatusUpdate, updating]);

 const columns = React.useMemo(() => [
  {
    title: t("S.No"),
    key: "serial",
    width: 60,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (_, __, index) => index + 1,
  },
  {
    title: t("Assigned To"),
    dataIndex: "assigned_to_name",
    key: "assigned_to_name",
    width: 200,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (name, record) => (
      <div className="d-flex align-items-center">
        <div className="avatar avatar-sm rounded-circle bg-primary-light me-2 d-flex align-items-center justify-content-center">
          <i className="fa fa-user text-primary small"></i>
        </div>
        <span className="fw-medium text-truncate">{name || record.assigned_to || 'Unknown'}</span>
      </div>
    ),
  },
  {
    title: t("Assigned By"),
    dataIndex: "assigned_by_name",
    key: "assigned_by_name",
    width: 200,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (name, record) => (
      <div className="d-flex align-items-center">
        <div className="avatar avatar-sm rounded-circle bg-success-light me-2 d-flex align-items-center justify-content-center">
          <i className="fa fa-user-check text-success small"></i>
        </div>
        <span className="text-muted text-truncate">{name || record.assigned_by || 'Unknown'}</span>
      </div>
    ),
  },
  {
    title: t("Status"),
    dataIndex: "assigned_task_status",
    key: "assigned_task_status",
    align: "center",
    width: 140,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (status, record) => getAssignmentStatusBadge(status, record.assignment_id),
  },
  {
    title: t("Payment"),
    dataIndex: "assigned_payment_status",
    key: "assigned_payment_status",
    align: "center",
    width: 140,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (status, record) => getAssignmentPaymentBadge(status, record.assignment_id),
  },
  {
    title: t("Date"),
    dataIndex: "assigned_at",
    key: "assigned_at",
    width: 120,
    responsive: ["xs", "sm", "md", "lg", "xl"],
    render: (date) => (
      <span className="text-muted small">
        {date
          ? new Date(date).toLocaleDateString("en-US", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : t("Not specified")}
      </span>
    ),
  },
], [t, getAssignmentStatusBadge, getAssignmentPaymentBadge]);


  if (loading) {
    return <Loading size="xl" variant="dots" />;
  }

  if (!task) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center mt-5">
            <h4>{t("Task not found")}</h4>
            <Button
              variant="primary"
              onClick={() => navigate("/employees/create-task")}
            >
              {t("Back to Tasks")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Task Management")}
          title={t("Dashboard")}
          subtitle={t("Task Details")}
          name={task.title}
        />

        {/* Task Information Card */}
        <div className="row">
          <div className="col-12">
            <div className="card mb-3">
              <div className="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <h4 className="card-title mb-2 mb-md-0">
                  <i className="fa fa-tasks me-2"></i>
                  {task.title}
                </h4>
                <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label text-muted small mb-0">
                      {t("Priority")}
                    </label>
                    <div>{getPriorityBadge(task.priority)}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label text-muted small mb-0">
                      {t("Deadline")}
                    </label>
                    <div className="small">
                      {task.deadline
                        ? new Date(task.deadline).toLocaleDateString()
                        : t("No deadline set")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body p-3">
                <div className="row">
                  <div className="col-12">
                    <div className="mb-2">
                      <label className="form-label text-muted small mb-1">
                        {t("Description")}
                      </label>
                      <p className="mb-0 small">
                        {task.description || t("No description provided")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assignment Details Card */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <h4 className="card-title mb-2 mb-md-0 p-0">
                  <i className="fa fa-users me-2"></i>
                  {t("Assignment Details")}
                </h4> 
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <Table
                    className="table-striped"
                    columns={columns}
                    dataSource={Array.isArray(task.assignments) ? task.assignments : []}
                    rowKey="assignment_id"
                    loading={updating}
                    scroll={{ x: 800 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      responsive: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    locale={{
                      emptyText: (
                        <div className="text-center py-5 w-50 mx-auto">
                          <div className="empty-state-box border rounded p-4 bg-light">
                            <i className="fa fa-user-times fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted mb-2">
                              {t("No assignments found")}
                            </h5>
                            <p className="text-muted mb-0">
                              {t("This task has not been assigned to anyone yet.")}
                            </p>
                          </div>
                        </div>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
